import { apiFetch } from "../../services/api";
import { isGoogleSsoConfigured, signInWithGoogleIdToken } from "./firebaseApp";

export { isGoogleSsoConfigured };

export function parseAuthApiError(errorData, fallback = "Request failed") {
  if (!errorData?.detail) return fallback;
  if (typeof errorData.detail === "string") return errorData.detail;
  if (typeof errorData.detail === "object" && errorData.detail.message) {
    return errorData.detail.message;
  }
  return fallback;
}

export function getAuthErrorCode(errorData) {
  if (errorData?.detail && typeof errorData.detail === "object") {
    return errorData.detail.code || "";
  }
  return "";
}

/**
 * Exchange Firebase ID token (+ optional trial signup fields) for DecisionPen JWT.
 */
export async function exchangeGoogleSso(body) {
  const response = await apiFetch("/api/auth/sso/exchange", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { response, data };
}

/**
 * Popup Google → exchange. Returns:
 * { ok: true, data }
 * { needsSignupDetails: true, idToken, email, displayName }
 * { ok: false, message }
 */
export async function continueWithGoogle(extra = {}) {
  if (!isGoogleSsoConfigured()) {
    return {
      ok: false,
      message:
        "Google sign-in is not configured for this environment. Use email and password, or contact support.",
    };
  }

  let google;
  try {
    google = await signInWithGoogleIdToken();
  } catch (err) {
    const code = err?.code || "";
    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      return { ok: false, message: "Google sign-in was cancelled." };
    }
    return {
      ok: false,
      message: err?.message || "Google sign-in failed. Please try again.",
    };
  }

  const { response, data } = await exchangeGoogleSso({
    id_token: google.idToken,
    ...extra,
  });

  if (response.ok) {
    return { ok: true, data };
  }

  const code = getAuthErrorCode(data);
  if (response.status === 409 || code === "AUTH_SSO_NEEDS_SIGNUP_DETAILS") {
    return {
      needsSignupDetails: true,
      idToken: google.idToken,
      email: google.email,
      displayName: google.displayName,
      message: parseAuthApiError(data, "Complete signup to continue"),
    };
  }

  return {
    ok: false,
    message: parseAuthApiError(data, "Google sign-in failed"),
  };
}

export function persistAuthSession(data) {
  if (data?.access_token) {
    localStorage.setItem("access_token", data.access_token);
  }
  if (data?.user) {
    localStorage.setItem("user", JSON.stringify(data.user));
  }
}
